import { Logger, LoggerService } from '@Shared/logger';
import {
  Auth0Client,
  GetTokenSilentlyVerboseResponse,
  User,
} from '@auth0/auth0-spa-js';
import { SETTINGS } from '@Config';
import {
  AUTH0_CONST,
  Auth0LoginResponse,
  Auth0Options,
  Auth0Repository,
  Auth0ServereAuthResult,
  AuthenticationUtils,
  AuthProfile,
  Bus,
  GetTokenSilentlyCacheModes,
  OptionsModule,
  PipelineModule,
} from '@Modules/index';
import { ENTITLEMENTS_CONST } from '@Modules/entitlement_module/_models/entitlements.constants';
import { APP_EVENTS, STORAGE } from '@Core';
import {
  CheckAuthStatusStep,
  CheckAuthStatusStepResultData,
} from '@Modules/pipeline_module/steps';
import { HttpService } from '@Shared/http/http.service';
import { JwtUtil, UrlUtil } from '@Shared/utils';
import { UIModule } from '@Modules/ui_module';

export class Auth0Service {
  private Logger: Logger = LoggerService.createLogger('Auth0Service');
  private static instance: Auth0Service;
  static get Instance() {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new Auth0Service();
    }
    return this.instance;
  }

  private auth0Options: Auth0Options;
  get Auth0Options(): Auth0Options {
    return this.auth0Options;
  }

  private auth0Repo: Auth0Repository;

  get Auth0Repo(): Auth0Repository {
    return this.auth0Repo;
  }

  private authClient: Auth0Client;
  get AuthClient(): Auth0Client {
    return this.authClient;
  }

  get shouldCheckPreAuthToken() {
    const query = window.location.search;
    const shouldCheckPreAuthToken = query.includes(
      `${AUTH0_CONST.checkPreAuthParam}=true`,
    );
    return shouldCheckPreAuthToken;
  }

  get targetAuth0LoginConnection() {
    if(!window.location.pathname.toLowerCase().startsWith("/login")) {
      return null
    }
    return new URLSearchParams(window.location.search).get("login-with")
  }

  get externalAuthRedirectUrl() {

    if(!this.targetAuth0LoginConnection) return null

    const continueUrl = `${window.location.origin}?${AUTH0_CONST.checkPreAuthParam}=true`
    const currentSubdomain = window.location.hostname.split(".")[0]
    let groupSubsDomain = "access.medianewsgroup.com"
    if(currentSubdomain == "develop" || currentSubdomain == "staging") {
      groupSubsDomain = "dev1-access.mng-digisubs-dev.com"
    } else if(currentSubdomain == "preprod") {
      groupSubsDomain = "preprod-access.mng-digisubs-dev.com"
    }

    return `https://${groupSubsDomain}/login?continue=${encodeURIComponent(continueUrl)}&connection=${encodeURIComponent(this.targetAuth0LoginConnection)}`

  }

  get shouldClearUser() {
    return UrlUtil.hasQueryParam(AUTH0_CONST.clearUserParam);
  }

  get shouldClearEntitlementsCache() {
    return UrlUtil.hasQueryParam(ENTITLEMENTS_CONST.clearEntitlementsCache);
  }

  get shouldParseResult() {
    const query = window.location.search;
    const shouldParseResult =
      query.includes('code=') && query.includes('state=');
    return shouldParseResult;
  }

  get shouldStartAmpRegwall() {
    return UrlUtil.hasQueryParam(AUTH0_CONST.ampRegiWallParam);
  }

  get shouldHandleLogoutPath() {
    return window.location.pathname.startsWith('/logout');
  }

  get shouldHandleLoginPath() {
    return window.location.pathname.startsWith('/login');
  }

  public isAuthenticated(): Promise<boolean> {
    return this.authClient.isAuthenticated();
  }

  constructor() {
    this.Logger.trace('****  CTOR ****');
  }

  public setup(options: Auth0Options): Promise<void> {
    this.Logger.trace('setting up Auth0 Client...');

    this.auth0Options = options;

    this.authClient = new Auth0Client({
      domain: this.auth0Options.domain,
      clientId: this.auth0Options.clientId,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      authorizationParams: {
        audience: SETTINGS.auth0.audience,
        scope: SETTINGS.auth0.scopes,
      },
    });
    this.Logger.trace('Done setting up Auth0 Client...');
    this.auth0Repo = new Auth0Repository();

    if(this.targetAuth0LoginConnection) {
      const redirectUrl = this.externalAuthRedirectUrl
      if(redirectUrl) {
        window.location.href = redirectUrl
      } else {
        this.Logger.warn("Invalid login redirect for " + targetConnection)
      }
    }

    return Promise.resolve();
  }

  public async getUser(): Promise<User> {
    if (this.auth0Repo.Auth0User) {
      this.Logger.trace('getUser: using cached user', this.auth0Repo.Auth0User);
      return this.auth0Repo.Auth0User;
    }
    const user = await this.authClient.getUser();
    this.Logger.trace('getUser from sdk call', user);
    if (user) {
      this.auth0Repo.Auth0User = user;

      return user;
    }
    return null;
  }

  /**
   * Builds an AuthProfile directly from a raw idToken (JWT). Used when the Auth0
   * SDK has no local session (e.g. cookies/localStorage were cleared) but a session
   * was restored from the session server. The idToken carries the standard OIDC
   * claims (sub, email, name, picture, etc.) needed to populate the profile, and
   * mapAuth0User caches it so subsequent getUser()/getAuthProfile() calls succeed.
   */
  public buildProfileFromIdToken(idToken: string): AuthProfile {
    if (!idToken) {
      return null;
    }
    try {
      const decoded = JwtUtil.decodeToken(idToken) as User;
      this.auth0Repo.setIdToken(idToken);
      return this.auth0Repo.mapAuth0User(decoded);
    } catch (error) {
      this.Logger.warn(
        'buildProfileFromIdToken failed to decode idToken',
        error,
      );
      return null;
    }
  }

  public async getAuthProfile(): Promise<AuthProfile> {
    if (this.auth0Repo.AuthProfile) {
      this.Logger.trace(
        'getUser: using cached user',
        this.auth0Repo.AuthProfile,
      );
      return this.auth0Repo.AuthProfile;
    }
    const user = await this.authClient.getUser();
    this.Logger.trace('getUser from sdk call', user);

    return user ? this.auth0Repo.mapAuth0User(user) : null;
  }

  public async getAuthState(): Promise<CheckAuthStatusStepResultData> {
    const cacheMode = await this.determineCacheMode();
    let isAuthenticated = await this.getTokenSilently(cacheMode);
    if (!isAuthenticated && this.shouldCheckPreAuthToken) {
      this.Logger.trace(
        'shouldCheckPreAuthToken is true, checking pre-auth token',
      );
      UIModule.ScreenManager.showScreen('preAuthCheck');
      UrlUtil.removeQueryParam(AUTH0_CONST.checkPreAuthParam);
      UrlUtil.removeQueryParam(AUTH0_CONST.preAuthedToken);
      const res = await this.getTokenWithPopup();
      isAuthenticated = res;
    }

    return {
      isAuthenticated: isAuthenticated,
      cacheMode: cacheMode,
      AuthProfile: await this.getAuthProfile(),
    };
  }

  public async loginWithSocial(action: string): Promise<boolean> {
    AuthenticationUtils.removeSSPaywallCookie();
    const provider = this.getSocialConnection(action);
    return this.authClient
      .loginWithPopup({ authorizationParams: { connection: provider } })
      .then(async (result) => {
        this.Logger.trace(
          'loginWithSocial => provider = ',
          provider,
          '  result',
          result,
        );
        //TODO: I think I neeed to call getTokenSilently here to get the token and then set it in the repo.
        localStorage.removeItem(STORAGE.notAuthenticatedCheckSessionKey);
        const showEntitlementCheckScreen =
          OptionsModule.Options.entitlement.check &&
          OptionsModule.Options.entitlement.showCheckScreenOnSocialLogin;
        const resContext = await PipelineModule.startUserInitiatedAuthPipeline(
          new CheckAuthStatusStep(),
          showEntitlementCheckScreen,
        );
        this.Logger.trace('loginWithSocial => resContext', resContext);
        if (resContext.checkAuthStatus.isAuthenticated) {
          const authMode = OptionsModule.Options.auth.mode;
          if (authMode.type === 'embedded' && authMode.options.reloadOnAuth && !resContext.isAuthFromPayway) {
            this.Logger.trace('Reloading page after login');
            location.reload();
          } else {
            this.Logger.trace(
              'Not Reloading Page On Login, just firing onReady event',
            );
            Bus.emit(APP_EVENTS.onReady, {
              auth: resContext.checkAuthStatus,
              entitlement: resContext?.checkEntitlements,
            });
          }
        }

        return !!resContext.checkAuthStatus?.isAuthenticated;
      })
      .catch((error) => {
        this.Logger.warn('loginWithSocial.error', error);
        return false;
      });
  }

  //TODO: Create common method for login/register since 95% of the code between these two methods is identical
  public async login(
    email: string,
    password: string,
  ): Promise<Auth0LoginResponse> {
    this.Logger.trace('Logging in with Auth0...');
    AuthenticationUtils.removeSSPaywallCookie();

    if (window.MNGAuthentication && window.dataLayer) {
      window.MNGAuthentication.preAuthGTMEvents.forEach((eventName) => {
        try {
          window.dataLayer!.push({
            event: eventName,
          });
          this.Logger.trace(`ANALYTICS: ${eventName} event`);
          this.Logger.trace('ANALYTICS: GA/ datalayer ', window.dataLayer);
        } catch (err) {
          this.Logger.error(
            `ANALYTICS: Failed to send ${eventName} event: `,
            err,
          );
        }
      });
    } else {
      this.Logger.trace(
        `ANALYTICS: Skipping preAuthGTMEvents publish. Not ready.`,
      );
    }

    const body = {
      email: email,
      password: password,
      domain: OptionsModule.Options.domain,
    };
    let response: Auth0LoginResponse;
    try {
      const httpService = new HttpService(SETTINGS.entitler.baseUrl, 0);
      Bus.emit(APP_EVENTS.onLoginStarted);
      response = await httpService.post2<Auth0ServereAuthResult>(
        'auth0/users/login',
        body,
        SETTINGS.entitler.apiKey,
      );
      this.Logger.trace('login.response', response);
      if (response.success) {
        this.auth0Repo.createLocalAuthObjects(response);
        localStorage.removeItem(STORAGE.notAuthenticatedCheckSessionKey);
        this.Logger.trace(
          'login successfully...checking getTokenSilently',
          response,
        );
        //calling this here again so that the Auth0 local storage object is created now and not on next page load.
        await this.getTokenSilently('on');

        Bus.emit(APP_EVENTS.onLogin, {
          page: location.href,
          result: response,
          user: await this.getAuthProfile(),
        });
      }
      return Promise.resolve(response);
    } catch (error) {
      this.Logger.error('Error making login call:', error);
      Bus.emit(APP_EVENTS.onLogin, { page: location.href, result: response });
      Promise.reject(error);
    }
  }

  //TODO: Create common method for login/register since 95% of the code between these two methods is identical
  public async register(
    email: string,
    password: string,
  ): Promise<Auth0LoginResponse> {
    this.Logger.trace('Logging in with Auth0...');
    AuthenticationUtils.removeSSPaywallCookie();

    const body = {
      email: email,
      password: password,
      domain: OptionsModule.Options.domain,
    };
    let response: Auth0LoginResponse;
    try {
      const httpService = new HttpService(SETTINGS.entitler.baseUrl, 0);
      Bus.emit(APP_EVENTS.onRegisterStarted);
      response = await httpService.post2<Auth0ServereAuthResult>(
        'auth0/users/register',
        body,
        SETTINGS.entitler.apiKey,
      );
      this.Logger.trace('login.response', response);
      if (response.success) {
        this.auth0Repo.createLocalAuthObjects(response);
        localStorage.removeItem(STORAGE.notAuthenticatedCheckSessionKey);
        this.Logger.trace(
          'registration successfully...checking getTokenSilently',
          response,
        );
        //calling this here again so that the Auth0 local storage object is created now and not on next page load.
        await this.getTokenSilently('on');

        Bus.emit(APP_EVENTS.onRegister, {
          page: location.href,
          result: response,
          user: await this.getAuthProfile(),
        });
      }
      return Promise.resolve(response);
    } catch (error) {
      this.Logger.error('Error making login call:', error);
      Bus.emit(APP_EVENTS.onRegister, {
        page: location.href,
        result: response,
      });
      Promise.reject(error);
    }
  }

  public async logout(): Promise<void> {
    this.Logger.trace('logout');
    AuthenticationUtils.removeSSPaywallCookie();
    localStorage.removeItem(STORAGE.notAuthenticatedCheckSessionKey);
    localStorage.removeItem(STORAGE.engageAuth0Sub);
    // The event must precede clearAuthData() so that entitlement clear can make use of the auth0 sub first
    Bus.emitAndTrack(APP_EVENTS.onLogout, { page: location.href }, 'onLogout');
    this.auth0Repo.clearAuthData();
    //this checks if the redirectUri is a javascript function, if so it will call it and use the result as the redirectUri. This is needed because json files from AWS don't let you have normal JS function names like window.location.origin. You need to set it as a string like this javascript:window.location.origin.
    const redirectUri = this.auth0Options.logout.redirectUri.includes(
      'javascript',
    )
      ? eval(this.auth0Options.logout.redirectUri)
      : this.auth0Options.logout.redirectUri;
    if (this.auth0Options.logout.clearServerCache) {
      //TODO: add something like an action=logout param and then check for this in case I need to do something. If I do, I should remove it using window.history after I complete whatever I need to do.
      this.Logger.trace(
        `Clearing server cache on logout, redirecting to ${redirectUri}`,
      );
      await this.authClient.logout({ logoutParams: { returnTo: redirectUri } });
    }
  }

  //PRIVATE METHODS

  private async getTokenSilently(
    cacheMode: GetTokenSilentlyCacheModes,
  ): Promise<boolean> {
    return this.authClient
      .getTokenSilently({
        detailedResponse: true,
        cacheMode: cacheMode,
        timeoutInSeconds: 5,
        authorizationParams: { redirect_uri: this.auth0Options.callbackURL },
      })
      .then((result: GetTokenSilentlyVerboseResponse) => {
        if (result) {
          this.auth0Repo.setIdToken(result.id_token);
          this.auth0Repo.setAccessToken(result.access_token);
        }
        return result ? true : false;
      })
      .catch((error) => {
        this.Logger.warn(
          'getTokenSilently',
          error.message,
          error.error,
          error.error_description,
        );
        return false;
      });
  }

  public async loginWithRedirect(): Promise<void> {
    AuthenticationUtils.removeSSPaywallCookie();
    await this.authClient.loginWithRedirect({
      authorizationParams: {
        prompt: 'none',
        redirect_uri: window.location.origin + window.location.pathname,
      },
      appState: { returnTo: window.location.origin + window.location.pathname },
    });
  }

  public async loginViaOneTap(
    email: string,
    useTribMainGoogle?: boolean,
  ): Promise<void> {
    AuthenticationUtils.removeSSPaywallCookie();
    const connection = useTribMainGoogle ? 'Trib-Google' : 'google-oauth2';
    this.Logger.trace('loginViaOneTap', { connection, email });

    try {
      await this.authClient.loginWithPopup({
        authorizationParams: {
          connection,
          login_hint: email,
        },
      });
      localStorage.removeItem(STORAGE.notAuthenticatedCheckSessionKey);
      const showEntitlementCheckScreen =
        OptionsModule.Options.entitlement.check &&
        OptionsModule.Options.entitlement.showCheckScreenOnSocialLogin;
      const resContext = await PipelineModule.startUserInitiatedAuthPipeline(
        new CheckAuthStatusStep(),
        showEntitlementCheckScreen,
      );
      this.Logger.trace('loginViaOneTap => resContext', resContext);
      if (resContext.checkAuthStatus.isAuthenticated) {
        const authMode = OptionsModule.Options.auth.mode;
        if (authMode.type === 'embedded' && authMode.options.reloadOnAuth) {
          this.Logger.trace('Reloading page after One Tap login');
          location.reload();
        } else {
          Bus.emit(APP_EVENTS.onReady, {
            auth: resContext.checkAuthStatus,
            entitlement: resContext?.checkEntitlements,
          });
        }
      }
    } catch (error) {
      this.Logger.warn('loginViaOneTap.error', error);
    }
  }

  public async loginViaAmpRegwall(): Promise<void> {
    AuthenticationUtils.removeSSPaywallCookie();
    const ampRegiWall = UrlUtil.getQueryParam(AUTH0_CONST.ampRegiWallParam);
    const prefId = UrlUtil.getQueryParam(AUTH0_CONST.prefIdParam);
    const sourceDomain = window.location.hostname
      .replace('www.', '')
      .replace('preprod.', '')
      .replace('develop.', '')
      .replace('staging.', '');

    this.Logger.trace('loginViaAmpRegwall', {
      ampRegiWall,
      sourceDomain,
      prefId,
    });

    await this.authClient.loginWithRedirect({
      authorizationParams: {
        redirect_uri: window.location.origin + '/login',
        ampRegiWall,
        sourceDomain,
        preferenceId: prefId,
        initialScreen: 'signUp',
      } as any,
    });
  }

  public async getTokenWithPopup(): Promise<boolean> {
    return this.authClient
      .getTokenWithPopup({ authorizationParams: { prompt: 'none' } })
      .then(async (result) => {
        this.Logger.trace('getTokenWithPopup.token', result);
        if (result) {
          return await this.getTokenSilently('on');
        } else {
          return false;
        }
      })
      .catch(async (error) => {
        //this.Logger.warn("getTokenWithPopup", error.message, error.error, error.error_description);
        if (error.message.includes('Unable to open a popup')) {
          this.Logger.warn('Popup blocked');
          await this.loginWithRedirect();
        }
        return false;
      });
  }

  //TODO: Update this with a better Type so I know what cachemode is used and why. Don't really need it, but it would be good for logging and analytics
  private async determineCacheMode(): Promise<GetTokenSilentlyCacheModes> {
    const isAuthenticated = await this.isAuthenticated();
    return isAuthenticated
      ? 'on'
      : this.auth0Repo.hasCheckedSession()
        ? 'cache-only'
        : 'off';
  }

  private getSocialConnection(action: string): string {
    const isTrib = this.auth0Options.isTrib;
    if (isTrib) {
      switch (action) {
        case 'google-oauth2':
          return 'Trib-Google';
        case 'facebook':
          return 'Trib-Facebook';
        case 'apple':
          return 'Trib-Apple';
      }
    } else {
      return action;
    }
  }
}
